import { TOD_MIN_START } from '../survival/DayClock';
import { HP_START, HUNGER_START, THIRST_START } from '../survival/Vitals';

export const SAVE_VERSION = 1;
export const IDB_NAME = 'ds';
export const IDB_STORE = 'slots';
export const IDB_KEY = 'slot.0';
/** localStorage index only — never the payload. */
export const SLOTS_KEY = 'ds.slots';
export const AUTO_SAVE_S = 60;

export type SaveFlags = Record<string, number | boolean | string>;
export type SaveInv = { id: string; count: number };

export type SaveV1 = {
  version: 1;
  x: number;
  y: number;
  z: number;
  yaw: number;
  hp: number;
  hunger: number;
  thirst: number;
  inventory: SaveInv[];
  recipes: string[];
  flags: SaveFlags;
  shelter: boolean;
  fireLit: boolean;
  deaths: number;
  todMinutes: number;
};

export type SaveStorage = {
  get(key: string): Promise<unknown>;
  set(key: string, value: unknown): Promise<void>;
};

export function createMemoryStorage(initial: Record<string, unknown> = {}): SaveStorage {
  const data = new Map<string, unknown>(Object.entries(initial));
  return {
    async get(key) {
      return data.has(key) ? data.get(key) : null;
    },
    async set(key, value) {
      data.set(key, value);
    },
  };
}

function hasIdb(): boolean {
  try {
    return typeof indexedDB !== 'undefined' && !!indexedDB;
  } catch {
    return false;
  }
}

function openIdb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(IDB_STORE)) {
        req.result.createObjectStore(IDB_STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('idb'));
  });
}

export async function createIdbStorage(): Promise<SaveStorage> {
  const db = await openIdb();
  return {
    get(key) {
      return new Promise((resolve, reject) => {
        const req = db.transaction(IDB_STORE, 'readonly').objectStore(IDB_STORE).get(key);
        req.onsuccess = () => resolve(req.result ?? null);
        req.onerror = () => reject(req.error ?? new Error('idb get'));
      });
    },
    set(key, value) {
      return new Promise((resolve, reject) => {
        const req = db.transaction(IDB_STORE, 'readwrite').objectStore(IDB_STORE).put(value, key);
        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error ?? new Error('idb put'));
      });
    },
  };
}

async function resolveStorage(inject?: SaveStorage): Promise<SaveStorage> {
  if (inject) {
    return inject;
  }
  try {
    if (hasIdb()) {
      return await createIdbStorage();
    }
  } catch {
    // private mode / missing IDB → memory
  }
  return createMemoryStorage();
}

function writeSlotsIndex(): void {
  try {
    const ls = globalThis.localStorage;
    if (!ls || typeof ls.setItem !== 'function') {
      return;
    }
    ls.setItem(SLOTS_KEY, JSON.stringify({ active: IDB_KEY, slots: [IDB_KEY], t: Date.now() }));
  } catch {
    // index is best-effort
  }
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function isFlagValue(value: unknown): value is number | boolean | string {
  return typeof value === 'boolean' || typeof value === 'string' || isFiniteNumber(value);
}

function parseInventory(raw: unknown): SaveInv[] | null {
  if (!Array.isArray(raw)) {
    return null;
  }
  const out: SaveInv[] = [];
  for (const row of raw) {
    if (!row || typeof row !== 'object' || Array.isArray(row)) {
      continue;
    }
    const item = row as Record<string, unknown>;
    if (typeof item.id !== 'string' || !item.id || !isFiniteNumber(item.count) || item.count <= 0) {
      continue;
    }
    out.push({ id: item.id, count: item.count });
  }
  return out;
}

function parseRecipes(raw: unknown): string[] | null {
  if (!Array.isArray(raw)) {
    return null;
  }
  const out: string[] = [];
  for (const id of raw) {
    if (typeof id === 'string' && id && !out.includes(id)) {
      out.push(id);
    }
  }
  return out;
}

function parseFlags(raw: unknown): SaveFlags | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return null;
  }
  const flags: SaveFlags = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (isFlagValue(value)) {
      flags[key] = value;
    }
  }
  return flags;
}

export function parseSave(raw: unknown): SaveV1 | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return null;
  }
  const data = raw as Record<string, unknown>;
  if (data.version !== SAVE_VERSION) {
    return null;
  }
  if (!isFiniteNumber(data.x) || !isFiniteNumber(data.y) || !isFiniteNumber(data.z) || !isFiniteNumber(data.yaw)) {
    return null;
  }
  if (!isFiniteNumber(data.hp) || !isFiniteNumber(data.hunger) || !isFiniteNumber(data.thirst)) {
    return null;
  }
  if (typeof data.shelter !== 'boolean' || typeof data.fireLit !== 'boolean') {
    return null;
  }
  if (!isFiniteNumber(data.deaths) || !isFiniteNumber(data.todMinutes)) {
    return null;
  }
  const inventory = parseInventory(data.inventory);
  const recipes = parseRecipes(data.recipes);
  const flags = parseFlags(data.flags);
  if (!inventory || !recipes || !flags) {
    return null;
  }
  return {
    version: 1,
    x: data.x,
    y: data.y,
    z: data.z,
    yaw: data.yaw,
    hp: data.hp,
    hunger: data.hunger,
    thirst: data.thirst,
    inventory,
    recipes,
    flags,
    shelter: data.shelter,
    fireLit: data.fireLit,
    deaths: data.deaths,
    todMinutes: data.todMinutes,
  };
}

export function blankSave(): SaveV1 {
  return {
    version: 1,
    x: 0,
    y: 1,
    z: 0,
    yaw: 0,
    hp: HP_START,
    hunger: HUNGER_START,
    thirst: THIRST_START,
    inventory: [],
    recipes: [],
    flags: {},
    shelter: false,
    fireLit: false,
    deaths: 0,
    todMinutes: TOD_MIN_START,
  };
}

function normalize(state: SaveV1): SaveV1 {
  return parseSave(state) ?? blankSave();
}

export class SaveGame {
  private readonly ready: Promise<SaveStorage>;

  constructor(storage?: SaveStorage) {
    this.ready = resolveStorage(storage);
  }

  async save(state: SaveV1): Promise<boolean> {
    try {
      const store = await this.ready;
      await store.set(IDB_KEY, normalize(state));
      writeSlotsIndex();
      return true;
    } catch {
      return false;
    }
  }

  async load(): Promise<SaveV1 | null> {
    try {
      const store = await this.ready;
      return parseSave(await store.get(IDB_KEY));
    } catch {
      return null;
    }
  }
}
